<script setup>
import { computed, ref, watch } from 'vue';
import { useTicketStore } from '@/stores/ticket';
import { useCommunityInteractionStore } from '@/stores/community-interactions';
import { Chip } from '@dashhudson/dashing-ui';
import capitalize from 'lodash/capitalize';
import { useCommunityCommentsStore } from '@/stores/community-comments';

const props = defineProps({
  /**
   * ID of the conversation message to get assigned tickets
   */
  messageId: { type: [Number, String], default: undefined },
});

const interactionStore = useCommunityInteractionStore();
const ticketStore = useTicketStore();
const commentStore = useCommunityCommentsStore();

const interaction = computed(() => interactionStore.selectedCommunityInteraction);
const comment = computed(() => commentStore.commentDetail);

const tickets = computed(() => {
  if (props.messageId) {
    return interaction.value?.tickets?.filter((t) => t.messageId === props.messageId);
  }
  if (comment.value) {
    if (comment.value.replies?.length > 0 && interaction.value?.id) {
      const matchingReply = comment.value.replies.find(
        (reply) => reply.id === interaction.value.id,
      );
      if (matchingReply?.tickets) {
        return matchingReply.tickets;
      }
    }
    return comment.value?.tickets;
  }
  return interaction.value?.tickets;
});

const ticketChips = ref([]);

async function loadTicketChips(ticketsToLoad = null) {
  const ticketsToUse = ticketsToLoad ?? tickets.value;
  if (!ticketsToUse?.length) {
    ticketChips.value = [];
    return;
  }
  ticketChips.value = await ticketStore.getTicketChips({
    tickets: ticketsToUse,
    communityInteraction: interaction.value,
  });
}

watch([tickets, interaction], () => loadTicketChips(), { immediate: true });
</script>

<template>
  <div v-if="ticketChips?.length" class="dash-flex dash-flex-wrap dash-items-center dash-gap-1">
    <template v-for="chip in ticketChips" :key="`${chip.source}${chip.label}`">
      <Chip
        :tooltip="chip.ticketTooltip"
        :class="{ 'dash-cursor-pointer': !!chip.ticketHref }"
        :label="chip.sourceName"
        :icon-right="!!chip.ticketHref ? 'link-external' : undefined"
        size="md-wide"
        hover-mode
        @click="chip.goToTicket"
      >
        <template #right>
          <Chip :label="capitalize(chip.status)" size="sm" severity="success" />
        </template>
      </Chip>
    </template>
  </div>
</template>
